import { request } from '../utils/request';
import type {
  DownloadSurveysRequestParams,
  SurveyType,
} from '../types/surveys.types';

export interface AlertNavigationResponse {
  uuid: string;
  type: SurveyType;
}

export const getNextAlert = async (
  uuid: string,
  currentType: SurveyType,
): Promise<AlertNavigationResponse | null> => {
  try {
    return await request<AlertNavigationResponse>(
      `/surveys/alerts/${uuid}/next?type=${currentType}`,
      {
        method: 'GET',
      },
    );
  } catch (error) {
    // Return null if no next alert found (404)
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
};

export const getPreviousAlert = async (
  uuid: string,
  currentType: SurveyType,
): Promise<AlertNavigationResponse | null> => {
  try {
    return await request<AlertNavigationResponse>(
      `/surveys/alerts/${uuid}/previous?type=${currentType}`,
      {
        method: 'GET',
      },
    );
  } catch (error) {
    // Return null if no previous alert found (404)
    if (error instanceof Error && error.message.includes('404')) {
      return null;
    }
    throw error;
  }
};

export const downloadSurveysAsCsv = async (
  params: DownloadSurveysRequestParams,
): Promise<void> => {
  // Build query string from params
  const queryParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  const url = `/surveys/download?${queryParams.toString()}`;

  // Use the request utility to get the blob
  const blob = await request<Blob>(url, {
    method: 'GET',
    responseType: 'blob',
  });

  // Create a download link and trigger it
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;
  link.download = `surveys-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
};

// A reference to a single survey across the three survey tables. Shared by
// every bulk action (each operates on a list of these), not just review.
export interface SurveyRef {
  uuid: string;
  type: SurveyType;
}

// A survey a bulk action couldn't apply to, with the server's reason code.
export interface SkippedSurvey extends SurveyRef {
  reason: string;
}

export const bulkMarkSurveysAsReviewed = async (
  surveys: SurveyRef[],
): Promise<{
  updated: SurveyRef[];
  skipped: SkippedSurvey[];
}> => {
  return request('/surveys/bulk-mark-reviewed', {
    method: 'POST',
    body: { surveys },
  });
};

export const undoBulkMarkSurveysAsReviewed = async (
  surveys: SurveyRef[],
): Promise<{
  reverted: SurveyRef[];
  skipped: SkippedSurvey[];
}> => {
  return request('/surveys/bulk-mark-reviewed/undo', {
    method: 'POST',
    body: { surveys },
  });
};
